import { createSlice } from '@reduxjs/toolkit'
import { membershipServices } from '../../services/Graphql'
import { OTP_EXPIRY_TIME } from '@common'
import { ErrorTrackingAPI } from '@services'
import {
    registerMemberAsync,
    requireOTPAsync,
    verifyEmailOTPAsync,
    getMemberAccessTokenAsync,
    refreshAccessTokenAsync,
    revokeRefreshTokenAsync,
    getCurrentMemberAsync,
    retrieveMemberOrderHistoryAsync,
    retrieveMemberUpcomingOrdersAsync,
    checkPendingRatingsAsync,
    submitDishRatingsAsync,
    cancelMemberOrderAsync,
} from '../thunk/membershipThunk'

const initialState = {
    // Authentication status
    isAuthenticated: false,
    isLoading: false,
    error: null,
    errorCode: null, // Error code for programmatic error handling

    // Member data
    memberData: {
        id: null,
        name: '',
        phone: '',
        email: '',
        status: '',
        points: 0,
        createdAt: '',
        updatedAt: '',
        stalls: [],
        assignments: [],
        credit: {
            totalAvailableCents: 0,
            expiringNextWeekCents: 0,
            availableBuckets: [],
            availableByScope: [],
            availableByUsage: [],
        },
    },

    // Email verification
    emailVerificationData: {
        email: '',
        isRegistration: false, // true: registration flow, false: resend verification flow
        password: null, // stored temporarily for auto-login after registration verification
        otpExpiry: null,
        verified: false,
        nextAvailableTime: null, // Next available time to resend OTP (from backend)
    },

    // Order history
    orderHistory: [],
    orderHistoryLoading: false,
    orderHistoryError: null,
    orderHistoryPageInfo: null,

    // Upcoming orders
    upcomingOrders: [],
    upcomingOrdersLoading: false,
    upcomingOrdersError: null,
    upcomingOrdersPageInfo: null,

    // Pending ratings
    pendingRatings: {
        hasPendingRatings: false,
        lastOrder: null,
        pendingItems: [],
    },
    pendingRatingsLoading: false,
    pendingRatingsError: null,

    // Cancel order
    cancelOrderLoading: false,
    cancelOrderError: null,
}

export const membershipSlice = createSlice({
    name: 'membership',
    initialState,
    reducers: {
        // Authentication actions
        setLoading: (state, action) => {
            state.isLoading = action.payload
        },
        setError: (state, action) => {
            state.error = action.payload
        },
        clearError: state => {
            state.error = null
            state.errorCode = null
        },

        // Member data actions
        setMemberData: (state, action) => {
            state.memberData = { ...state.memberData, ...action.payload }
            state.isAuthenticated = true
        },
        clearMemberData: state => {
            state.memberData = initialState.memberData
            state.isAuthenticated = false
        },
        updateCredit: (state, action) => {
            state.memberData.credit = action.payload
        },
        updatePoints: (state, action) => {
            state.memberData.points = action.payload
        },

        // Email verification actions
        setEmailVerificationData: (state, action) => {
            state.emailVerificationData = { ...state.emailVerificationData, ...action.payload }
        },
        clearEmailVerificationData: state => {
            state.emailVerificationData = initialState.emailVerificationData
        },
        setEmailVerified: (state, action) => {
            state.emailVerificationData.verified = action.payload
        },

        // Order history actions
        setOrderHistory: (state, action) => {
            state.orderHistory = action.payload
        },
        addOrderToHistory: (state, action) => {
            state.orderHistory.unshift(action.payload)
        },
        setOrderHistoryLoading: (state, action) => {
            state.orderHistoryLoading = action.payload
        },
        setOrderHistoryError: (state, action) => {
            state.orderHistoryError = action.payload
        },

        // Logout action
        logout: state => {
            state.isAuthenticated = false
            state.memberData = initialState.memberData
            state.emailVerificationData = initialState.emailVerificationData
            state.orderHistory = []
            state.orderHistoryPageInfo = null
            state.orderHistoryLoading = false
            state.orderHistoryError = null
            state.error = null
            state.isLoading = false // Stop any ongoing loading

            // Clear access token from localStorage
            membershipServices.clearTokens()
        },

        // Clear order history
        clearOrderHistory: state => {
            state.orderHistory = []
            state.orderHistoryPageInfo = null
            state.orderHistoryError = null
        },
    },
    extraReducers: builder => {
        builder
            /** registerMember */
            .addCase(registerMemberAsync.pending, state => {
                state.isLoading = true
                state.error = null
            })
            .addCase(registerMemberAsync.fulfilled, (state, action) => {
                state.isLoading = false
                // OTP will be automatically sent to email
                state.emailVerificationData.email = action.payload.email
                state.emailVerificationData.isRegistration = true
                state.emailVerificationData.otpExpiry = action.payload.otpExpiry || Date.now() + OTP_EXPIRY_TIME * 1000
                state.emailVerificationData.verified = false
            })
            .addCase(registerMemberAsync.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload
            })

            /** requireOTP */
            .addCase(requireOTPAsync.pending, state => {
                state.isLoading = true
                state.error = null
            })
            .addCase(requireOTPAsync.fulfilled, (state, action) => {
                state.isLoading = false
                // OTP will be automatically sent to email
                state.emailVerificationData.email = action.payload.email
                state.emailVerificationData.isRegistration = false
                state.emailVerificationData.otpExpiry = action.payload.otpExpiry || Date.now() + OTP_EXPIRY_TIME * 1000
                state.emailVerificationData.verified = false
                state.emailVerificationData.nextAvailableTime = action.payload.nextAvailableTime || null
            })
            .addCase(requireOTPAsync.rejected, (state, action) => {
                state.isLoading = false
                // Handle both string error and object with error/nextAvailableTime
                if (typeof action.payload === 'string') {
                    state.error = action.payload
                } else if (action.payload && typeof action.payload === 'object') {
                    state.error = action.payload.error || action.payload
                    // Update nextAvailableTime even when request failed
                    if (action.payload.nextAvailableTime) {
                        state.emailVerificationData.nextAvailableTime = action.payload.nextAvailableTime
                    }
                } else {
                    state.error = action.payload
                }
            })

            /** verifyEmailOTP */
            .addCase(verifyEmailOTPAsync.pending, state => {
                state.isLoading = true
                state.error = null
            })
            .addCase(verifyEmailOTPAsync.fulfilled, (state, action) => {
                state.isLoading = false
                state.emailVerificationData.verified = true
            })
            .addCase(verifyEmailOTPAsync.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload
            })

            /** getMemberAccessToken */
            .addCase(getMemberAccessTokenAsync.pending, state => {
                state.isLoading = true
                state.error = null
                state.errorCode = null
            })
            .addCase(getMemberAccessTokenAsync.fulfilled, (state, action) => {
                state.isLoading = false
                state.isAuthenticated = true
                state.emailVerificationData.verified = true
                state.error = null
                state.errorCode = null
            })
            .addCase(getMemberAccessTokenAsync.rejected, (state, action) => {
                state.isLoading = false
                // Handle error with errorCode
                if (action.payload && typeof action.payload === 'object') {
                    state.error = action.payload.error
                    state.errorCode = action.payload.errorCode
                } else {
                    state.error = action.payload
                    state.errorCode = null
                }
            })

            /** refreshAccessToken */
            .addCase(refreshAccessTokenAsync.pending, state => {
                // Don't set loading state for background refresh
                // state.isLoading = true
            })
            .addCase(refreshAccessTokenAsync.fulfilled, (state, action) => {
                // Token refreshed successfully, user remains authenticated
                state.isAuthenticated = true
                state.error = null
                state.errorCode = null
            })
            .addCase(refreshAccessTokenAsync.rejected, (state, action) => {
                // Refresh failed, user should be logged out
                state.isAuthenticated = false
                state.memberData = initialState.memberData
                // Handle error with errorCode
                if (action.payload && typeof action.payload === 'object') {
                    state.error = action.payload.error
                    state.errorCode = action.payload.errorCode
                } else {
                    state.error = action.payload
                    state.errorCode = null
                }
                // Clear Sentry user context when token refresh fails and user is logged out
                ErrorTrackingAPI.clearUserContext()
            })

            /** revokeRefreshToken */
            .addCase(revokeRefreshTokenAsync.pending, state => {
                state.isLoading = true
            })
            .addCase(revokeRefreshTokenAsync.fulfilled, (state, action) => {
                state.isLoading = false
                state.isAuthenticated = false
                state.memberData = initialState.memberData
                state.error = null
                state.errorCode = null
                // Clear Sentry user context on logout
                ErrorTrackingAPI.clearUserContext()
            })
            .addCase(revokeRefreshTokenAsync.rejected, (state, action) => {
                state.isLoading = false
                // Even if revoke fails, user is logged out (tokens cleared)
                state.isAuthenticated = false
                state.memberData = initialState.memberData
                // Handle error with errorCode
                if (action.payload && typeof action.payload === 'object') {
                    state.error = action.payload.error
                    state.errorCode = action.payload.errorCode
                } else {
                    state.error = action.payload
                    state.errorCode = null
                }
                // Clear Sentry user context on logout
                ErrorTrackingAPI.clearUserContext()
            })

            /** getCurrentMember */
            .addCase(getCurrentMemberAsync.pending, state => {
                state.isLoading = true
                state.error = null
            })
            .addCase(getCurrentMemberAsync.fulfilled, (state, action) => {
                state.isLoading = false
                // If we successfully get current member, user is authenticated
                if (action.payload.member) {
                    state.isAuthenticated = true
                    state.memberData = { ...state.memberData, ...action.payload.member }
                    // Set member context in Sentry
                    ErrorTrackingAPI.setMemberContext(action.payload.member)
                } else {
                    // If member is null, it means the member doesn't exist in current environment
                    // Force logout by clearing token and resetting state
                    state.isAuthenticated = false
                    state.memberData = initialState.memberData
                    membershipServices.clearTokens()
                    // Clear Sentry user context
                    ErrorTrackingAPI.clearUserContext()
                }
            })
            .addCase(getCurrentMemberAsync.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload
                // If getting current member fails, user might not be authenticated
                // Force logout by clearing token and resetting state
                state.isAuthenticated = false
                state.memberData = initialState.memberData
                membershipServices.clearTokens()
                // Clear Sentry user context
                ErrorTrackingAPI.clearUserContext()
            })

            /** retrieveMemberOrderHistory */
            .addCase(retrieveMemberOrderHistoryAsync.pending, state => {
                state.orderHistoryLoading = true
                state.orderHistoryError = null
            })
            .addCase(retrieveMemberOrderHistoryAsync.fulfilled, (state, action) => {
                state.orderHistoryLoading = false
                state.orderHistory = action.payload.orders
                state.orderHistoryPageInfo = action.payload.pageInfo
            })
            .addCase(retrieveMemberOrderHistoryAsync.rejected, (state, action) => {
                state.orderHistoryLoading = false
                state.orderHistoryError = action.payload
            })

            /** retrieveMemberUpcomingOrders */
            .addCase(retrieveMemberUpcomingOrdersAsync.pending, state => {
                state.upcomingOrdersLoading = true
                state.upcomingOrdersError = null
            })
            .addCase(retrieveMemberUpcomingOrdersAsync.fulfilled, (state, action) => {
                state.upcomingOrdersLoading = false
                state.upcomingOrders = action.payload.orders
                state.upcomingOrdersPageInfo = action.payload.pageInfo
            })
            .addCase(retrieveMemberUpcomingOrdersAsync.rejected, (state, action) => {
                state.upcomingOrdersLoading = false
                state.upcomingOrdersError = action.payload
            })

            /** checkPendingRatings */
            .addCase(checkPendingRatingsAsync.pending, state => {
                state.pendingRatingsLoading = true
                state.pendingRatingsError = null
            })
            .addCase(checkPendingRatingsAsync.fulfilled, (state, action) => {
                state.pendingRatingsLoading = false
                state.pendingRatings = {
                    hasPendingRatings: action.payload.hasPendingRatings || false,
                    lastOrder: action.payload.lastOrder || null,
                    pendingItems: action.payload.pendingItems || [],
                }
            })
            .addCase(checkPendingRatingsAsync.rejected, (state, action) => {
                state.pendingRatingsLoading = false
                state.pendingRatingsError = action.payload
            })

            /** submitDishRatings */
            .addCase(submitDishRatingsAsync.pending, state => {
                state.pendingRatingsLoading = true
                state.pendingRatingsError = null
            })
            .addCase(submitDishRatingsAsync.fulfilled, state => {
                state.pendingRatingsLoading = false
            })
            .addCase(submitDishRatingsAsync.rejected, (state, action) => {
                state.pendingRatingsLoading = false
                state.pendingRatingsError = action.payload
            })

            /** cancelMemberOrder */
            .addCase(cancelMemberOrderAsync.pending, state => {
                state.cancelOrderLoading = true
                state.cancelOrderError = null
            })
            .addCase(cancelMemberOrderAsync.fulfilled, state => {
                state.cancelOrderLoading = false
                state.cancelOrderError = null
            })
            .addCase(cancelMemberOrderAsync.rejected, (state, action) => {
                state.cancelOrderLoading = false
                state.cancelOrderError = action.payload
            })
    },
})

export const membershipActions = {
    ...membershipSlice.actions,
    registerMemberAsync,
    requireOTPAsync,
    verifyEmailOTPAsync,
    getMemberAccessTokenAsync,
    refreshAccessTokenAsync,
    revokeRefreshTokenAsync,
    getCurrentMemberAsync,
    retrieveMemberOrderHistoryAsync,
    retrieveMemberUpcomingOrdersAsync,
    checkPendingRatingsAsync,
    submitDishRatingsAsync,
    cancelMemberOrderAsync,
}

export default membershipSlice.reducer
